"use client";

import { Fragment } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "../Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../DropdownMenu";
import { cn } from "../../lib/utils";
import { MdKeyboardArrowRight } from "react-icons/md";
import { ProfileProps, ProfileMenuItem } from "./types";
import { Typography } from "../Typography";

export function Profile({
  user,
  menuItems,
  className,
  onMenuItemClick,
  modal = true,
  contentClassName,
}: ProfileProps) {
  return (
    <DropdownMenu modal={modal}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center focus:outline-none cursor-pointer",
            "md:border md:rounded-3xl md:pl-4 md:py-1 md:pr-1 md:gap-[10px] md:whitespace-nowrap",
            className
          )}
        >
          <Typography
            variant="default"
            color="gray500"
            weight="medium"
            size={"sm"}
            className="hidden md:block"
          >
            {user.name}
          </Typography>
          <Avatar className="h-7 w-7 md:h-9 md:w-9">
            {user.avatarUrl && (
              <AvatarImage src={user.avatarUrl} alt={user.name} />
            )}
            <AvatarFallback>
              {user.initials || user.name.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={cn(
          "w-72 shadow-profile-menu mt-1 select-none bg-white px-0 py-2 text-ticketon-blue",
          contentClassName
        )}
      >
        <ul className="flex list-none flex-col whitespace-nowrap text-sm uppercase">
          {menuItems.map((item: ProfileMenuItem) => (
            <Fragment key={item.id}>
              {item.type === "profile" ? (
                <li className="p-0">
                  <DropdownMenuItem
                    className="w-full h-full flex cursor-pointer p-0 data-[highlighted]:bg-transparent focus:bg-transparent"
                    onSelect={() => onMenuItemClick?.(item)}
                  >
                    <div className="whitespace-normal rounded-sm p-4 py-2">
                      <Typography
                        variant="default"
                        weight="medium"
                        size="base"
                        color="primary"
                        className="w-full overflow-hidden text-ellipsis uppercase"
                      >
                        {user.name}
                      </Typography>
                      <div className="flex items-center">
                        <Typography
                          variant="default"
                          size="xs"
                          color="primary"
                          className="flex items-center uppercase"
                        >
                          {item.label}
                          <MdKeyboardArrowRight className="h-4 w-4 ml-1" />
                        </Typography>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </li>
              ) : (
                <li className="p-0">
                  <DropdownMenuItem
                    className="p-0 h-auto data-[highlighted]:bg-transparent focus:bg-transparent"
                    onSelect={() => onMenuItemClick?.(item)}
                  >
                    <Typography
                      variant="default"
                      color="primary"
                      size="sm"
                      className="block h-full cursor-pointer rounded-sm p-4 tracking-wide transition-all lg:py-3 uppercase w-full"
                    >
                      {item.label}
                    </Typography>
                  </DropdownMenuItem>
                </li>
              )}

              {item.type === "profile" && (
                <div className="border-ticketon-lighter-gray w-full border-b my-2"></div>
              )}
            </Fragment>
          ))}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
